import React, { useEffect, useState } from "react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import { US_STATES, BUSINESS_TYPES } from "../../../data/rates.js";
import { useApp } from "../../../hooks/useApp.js";
import { deriveCtl, ctlSuggestions, sanitizeCtlInput, validateBiz, FIELD_FORMATS, sanitizeEmailInput, TEXT_TYPE_STRIP } from "../../../utils/validators.js";
import { formatPhone } from "../../../utils/format.js";
import { getToken } from "../../../services/api.js";
import { getBusiness, saveBusiness } from "../../../services/businessService.js";
import { listTaxPreparerOptions } from "../../../services/taxPreparerService.js";
import { getUsStates, getBusinessTypes, getCountries } from "../../../services/lookupService.js";

const DEFAULT_COUNTRY_OPTIONS = [{ isoCode: "US", name: "United States" }];

/* Same visual as BusinessesScreen's ErrorMsg — red text + icon under the field */
function ErrorMsg({ msg }) {
  if (!msg) return null;
  return (
    <p className="text-[11px] text-[#DC2626] mt-[4px] flex items-center gap-[4px]">
      <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="5.5" stroke="#DC2626" strokeWidth="1.2" />
        <path d="M6 4v3" stroke="#DC2626" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="6" cy="8.5" r="0.6" fill="#DC2626" />
      </svg>
      {msg}
    </p>
  );
}

function FormatHint({ text }) {
  if (!text) return null;
  return <p className="text-[11px] text-[var(--muted)] mt-[3px]">{text}</p>;
}

const BusinessStep = ({ onNext, onBack, continueLabel = "Continue Tax year →", backLabel = "← Back" }) => {
  const { state, dispatch, showToast } = useApp();
  const { biz, businessId } = state;
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stateOptions, setStateOptions] = useState(US_STATES.map((s) => [s, s]));
  const [businessTypeOptions, setBusinessTypeOptions] = useState(BUSINESS_TYPES);
  const [lookupsLoading, setLookupsLoading] = useState(true);
  const [countryOptions, setCountryOptions] = useState(DEFAULT_COUNTRY_OPTIONS);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [preparerOptions, setPreparerOptions] = useState([]);
  const [preparerOptionsLoading, setPreparerOptionsLoading] = useState(true);

  // errors[key] is a STRING message, or "" / undefined when the field is valid.
  const { errors } = validateBiz(biz);

  const setBiz = (patch) => dispatch({ type: "SET_BIZ", patch });
  const isSoleProp = biz.businessType === "sole_prop";
  const suggestions = isSoleProp ? ctlSuggestions(biz.name) : [];

  useEffect(() => {
    let cancelled = false;
    Promise.all([getUsStates(), getBusinessTypes()])
      .then(([states, types]) => {
        if (cancelled) return;
        if (Array.isArray(states) && states.length) {
          setStateOptions(states.map((s) => [s.code, s.name]));
        }
        if (Array.isArray(types) && types.length) {
          setBusinessTypeOptions(types.map((t) => [t.code, t.label]));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLookupsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    getCountries()
      .then((list) => {
        if (!cancelled && Array.isArray(list) && list.length) {
          setCountryOptions(list);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A saved foreign business only carries the country's name back from the
  // API (no isoCode) — resolve it against the loaded country list once both
  // are available, so the dropdown can preselect it.
  useEffect(() => {
    if (biz.country || !biz.countryName || !countryOptions.length) return;
    const match = countryOptions.find((c) => c.name === biz.countryName);
    if (match) setBiz({ country: match.isoCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOptions, biz.countryName, biz.country]);

  useEffect(() => {
    if (!businessId || !getToken()) return;
    let cancelled = false;
    getBusiness(businessId)
      .then((saved) => {
        if (!cancelled) setBiz(saved);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Options for the "Tax Preparer" dropdown - the caller's own saved
  // preparers. Requires auth (unlike the public state/business-type
  // lookups), so anonymous sessions just see an empty (optional) list.
  useEffect(() => {
    if (!getToken()) {
      setPreparerOptionsLoading(false);
      return;
    }
    let cancelled = false;
    listTaxPreparerOptions()
      .then((list) => {
        if (!cancelled) setPreparerOptions(list);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setPreparerOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Live input filtering ──
     - Leading space is blocked instantly (replace(/^ /, "")).
     - Trailing space is allowed while typing (needed for multi-word names)
       but stripped on blur via trimTrailing(). */

  const trimTrailing = (key) => () => {
    setBiz({ [key]: (biz[key] || "").replace(/\s+$/, "") });
  };

  const handleNameChange = (e) => {
    const name = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9#()&'\- ]/g, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 60);
    const patch = { name };
    // Keep prefilling until the filer types their own value (user-edited wins).
    if (!biz.ctl || biz.ctl === deriveCtl(biz.name)) patch.ctl = deriveCtl(name);
    setBiz(patch);
  };

  const handleEinChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 9);
    setBiz({ ein: digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits });
  };

  const handleEmailChange = (e) => {
    setBiz({ email: sanitizeEmailInput(e.target.value) });
  };

  const handleTitleChange = (e) => {
    const val = e.target.value.replace(/^ /, "").slice(0, 35);
    setBiz({ signingAuthorityTitle: val });
  };

  const handleSigningAuthorityNameChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9'\- ]/g, "")
      .replace(/ {2,}/g, " ")
      .replace(/-{2,}/g, "-")
      .replace(/'{2,}/g, "'")
      .slice(0, 35);
    setBiz({ signingAuthorityName: val });
  };

  const handleAddressChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9\-/ ]/g, "")
      .slice(0, 35);
    setBiz({ a1: val });
  };

  const handleAddress2Change = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9\-/ ]/g, "")
      .slice(0, 35);
    setBiz({ a2: val });
  };

  const handleCountryChange = (e) => {
    const iso = e.target.value;
    const country = countryOptions.find((c) => c.isoCode === iso);
    setBiz({
      country: iso,
      countryName: country?.name || "",
      kind: iso === "US" ? "us" : "foreign",
      state: "",
    });
  };

  // Non-US (Canada/Mexico): free-text Province/State - IRS TextType, max 17.
  const handleStateTextChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(TEXT_TYPE_STRIP, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 17);
    setBiz({ state: val });
  };

  const handleCityChange = (e) => {
    const isUs = !biz.country || biz.country === "US";
    const maxLen = isUs ? 22 : 50;
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z ]/g, "")
      .replace(/ {2,}/g, " ")
      .slice(0, maxLen);
    setBiz({ city: val });
  };

  const handleZipChange = (e) => {
    const isUs = !biz.country || biz.country === "US";
    if (isUs) {
      const digits = e.target.value.replace(/\D/g, "").slice(0, 12);
      setBiz({ zip: digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits });
      return;
    }
    // Non-US (Canada/Mexico): free-text postal code - IRS TextType, max 16.
    const val = e.target.value
      .replace(/^ /, "")
      .replace(TEXT_TYPE_STRIP, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 16);
    setBiz({ zip: val });
  };

  const handleTpdNameChange = (e) => {
    const val = e.target.value
      .replace(/^ /, "")
      .replace(/[^A-Za-z0-9'\- ]/g, "")
      .replace(/ {2,}/g, " ")
      .slice(0, 35);
    setBiz({ tpdName: val });
  };

  const handleContinue = async () => {
    setTouched(true);
    const { ok } = validateBiz(biz);
    if (!ok) {
      showToast("Please fix the highlighted fields.");
      return;
    }

    if (getToken()) {
      setSaving(true);
      try {
        const saved = await saveBusiness(businessId, biz);
        dispatch({ type: "SET_BUSINESS_ID", businessId: saved.id });
      } catch (err) {
        setSaving(false);
        showToast(err.message || "Couldn't save the business. Please try again.");
        return;
      }
      setSaving(false);
    }

    onNext();
  };

  // Only show a field's error once the user has attempted to submit.
  const show = (key) => touched && !!errors[key];

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Business
      </div>
      <h2 className="text-2xl m-[4px_0]">Tell us about the business</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-[18px]">
        This is the company the trucks are registered to. It must match IRS records.
      </p>

      <FormField label="Business legal name" required>
        <TextInput
          value={biz.name}
          onChange={handleNameChange}
          onBlur={trimTrailing("name")}
          placeholder="Ray's Hauling LLC"
          maxLength={60}
          invalid={show("name")}
        />
        <FormatHint text={FIELD_FORMATS.name} />
        <ErrorMsg msg={show("name") ? errors.name : ""} />
      </FormField>

      <FormField
        label={<>Tax Preparer <span className="text-[var(--dim)] font-normal">· optional</span></>}
        hint={preparerOptionsLoading ? "Loading options…" : "Reuse a preparer you've already saved, if this return has one."}
      >
        <Select
          value={biz.taxPreparerId || ""}
          onChange={(e) => setBiz({ taxPreparerId: e.target.value ? Number(e.target.value) : null })}
          disabled={preparerOptionsLoading}
        >
          <option value="">— None —</option>
          {preparerOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </FormField>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="EIN (Employer ID Number)" required>
          <TextInput
            mono
            maxLength={10}
            placeholder="12-3456789"
            value={biz.ein}
            onChange={handleEinChange}
            invalid={show("ein")}
          />
          <FormatHint text={FIELD_FORMATS.ein} />
          <ErrorMsg msg={show("ein") ? errors.ein : ""} />
        </FormField>
        <FormField label="Business type" required hint={lookupsLoading ? "Loading options…" : undefined}>
          <Select
            value={biz.businessType}
            onChange={(e) => setBiz({ businessType: e.target.value })}
            invalid={show("businessType")}
            disabled={lookupsLoading}
          >
            <option value="">—</option>
            {businessTypeOptions.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          <ErrorMsg msg={show("businessType") ? errors.businessType : ""} />
        </FormField>
      </div>

      <div className="grid gap-3.5 sm:grid-cols-2">
        {/* Sent to the IRS as-is, so the filer must be able to check it against
            the EIN letter. Prefilled from the business name (deriveCtl). Sole
            proprietors also get one choice per word of the name (ctlSuggestions):
            the IRS often uses their last name, which the name alone can't tell. */}
        <FormField
          label="Name control"
          required
          hint={
            isSoleProp
              ? "The code printed on your IRS EIN letter (CP 575). For sole proprietors the IRS often uses your last name - with two Spanish surnames, the first one."
              : "The code printed on your IRS EIN letter (CP 575). It is usually the first four letters of your business name."
          }
        >
          <TextInput
            mono
            maxLength={4}
            placeholder="RAYS"
            value={biz.ctl}
            onChange={(e) => setBiz({ ctl: sanitizeCtlInput(e.target.value) })}
            invalid={show("ctl")}
          />
          <FormatHint text={FIELD_FORMATS.ctl} />
          <ErrorMsg msg={show("ctl") ? errors.ctl : ""} />
          {isSoleProp && suggestions.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              <span className="text-xs text-[var(--muted)]">Pick one:</span>
              {suggestions.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setBiz({ ctl: c })}
                  aria-pressed={biz.ctl === c}
                  className={
                    "font-mono text-xs px-2.5 py-1 rounded-full border " +
                    (biz.ctl === c
                      ? "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]"
                      : "border-[var(--line)] text-[var(--ink)] hover:border-[var(--accent)]")
                  }
                >
                  {c}
                </button>
              ))}
            </div>
          )}
        </FormField>
        <FormField label="Email" required>
          <TextInput
            type="email"
            placeholder="office@rayshauling.com"
            value={biz.email}
            onChange={handleEmailChange}
            onBlur={() => setBiz({ email: (biz.email || "").trim() })}
            invalid={show("email")}
          />
          <FormatHint text={FIELD_FORMATS.email} />
          <ErrorMsg msg={show("email") ? errors.email : ""} />
        </FormField>
      </div>
{/* 
      <FormField label={<>In care of <span className="text-[var(--dim)] font-normal">· optional</span></>}>
        <TextInput
          placeholder="% Accounting dept"
          value={biz.care}
          onChange={(e) => setBiz({ care: e.target.value.replace(/^ /, "") })}
          onBlur={trimTrailing("care")}
        />
      </FormField> */}

      <FormField label="Business phone" required>
        <TextInput
          mono
          placeholder="(815) 555-0110"
          value={biz.phone}
          onChange={(e) => setBiz({ phone: formatPhone(e.target.value) })}
          invalid={show("phone")}
        />
        <FormatHint text={FIELD_FORMATS.phone} />
        <ErrorMsg msg={show("phone") ? errors.phone : ""} />
      </FormField>

      <h3 className="text-[15px] font-semibold mt-5 mb-1">Signing authority</h3>
      <p className="text-[var(--muted)] text-sm mb-3">The person authorized to sign this return on the business's behalf.</p>

      <FormField label="Signing authority name" required>
        <TextInput
          value={biz.signingAuthorityName}
          onChange={handleSigningAuthorityNameChange}
          onBlur={trimTrailing("signingAuthorityName")}
          placeholder="Ray Delgado"
          maxLength={35}
          invalid={show("signingAuthorityName")}
        />
        <FormatHint text={FIELD_FORMATS.signingAuthorityName} />
        <ErrorMsg msg={show("signingAuthorityName") ? errors.signingAuthorityName : ""} />
      </FormField>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="Title" required>
          <TextInput
            value={biz.signingAuthorityTitle}
            onChange={handleTitleChange}
            onBlur={trimTrailing("signingAuthorityTitle")}
            placeholder="Managing Member"
            maxLength={35}
            invalid={show("signingAuthorityTitle")}
          />
          <FormatHint text={FIELD_FORMATS.signingAuthorityTitle} />
          <ErrorMsg msg={show("signingAuthorityTitle") ? errors.signingAuthorityTitle : ""} />
        </FormField>
        <FormField label="Mobile phone" required>
          <TextInput
            mono
            placeholder="(815) 555-0110"
            value={biz.signingAuthorityPhone}
            onChange={(e) => setBiz({ signingAuthorityPhone: formatPhone(e.target.value) })}
            invalid={show("signingAuthorityPhone")}
          />
          <FormatHint text={FIELD_FORMATS.signingAuthorityPhone} />
          <ErrorMsg msg={show("signingAuthorityPhone") ? errors.signingAuthorityPhone : ""} />
        </FormField>
      </div>

      <h3 className="text-[15px] font-semibold mt-5 mb-1">Business address</h3>

      <FormField label="Address line 1" required>
        <TextInput
          placeholder="Street address"
          value={biz.a1}
          onChange={handleAddressChange}
          onBlur={trimTrailing("a1")}
          maxLength={35}
          invalid={show("a1")}
        />
        <FormatHint text={FIELD_FORMATS.a1} />
        <ErrorMsg msg={show("a1") ? errors.a1 : ""} />
      </FormField>

      <FormField label={<>Address line 2 <span className="text-[var(--dim)] font-normal">· optional</span></>}>
        <TextInput
          placeholder="Suite, unit, building"
          value={biz.a2}
          onChange={handleAddress2Change}
          onBlur={trimTrailing("a2")}
          maxLength={35}
          invalid={show("a2")}
        />
        <FormatHint text={FIELD_FORMATS.a2} />
        <ErrorMsg msg={show("a2") ? errors.a2 : ""} />
      </FormField>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="Country" required hint={countriesLoading ? "Loading options…" : undefined}>
          <Select
            value={biz.country}
            onChange={handleCountryChange}
            invalid={show("country")}
            disabled={countriesLoading}
          >
            <option value="">—</option>
            {countryOptions.map((c) => (
              <option key={c.isoCode} value={c.isoCode}>
                {c.name}
              </option>
            ))}
          </Select>
          <ErrorMsg msg={show("country") ? errors.country : ""} />
        </FormField>

        {biz.country === "US" ? (
          <FormField label="State" required>
            <Select
              value={biz.state}
              onChange={(e) => setBiz({ state: e.target.value })}
              invalid={show("state")}
              disabled={lookupsLoading}
            >
              <option value="">—</option>
              {stateOptions.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
            <ErrorMsg msg={show("state") ? errors.state : ""} />
          </FormField>
        ) : (
          <FormField
            label={biz.country === "CA" ? "Province" : biz.country === "MX" ? "State" : "State / Province"}
            required={!!biz.country}
          >
            <TextInput
              placeholder={
                biz.country === "CA" ? "e.g. Ontario" : biz.country === "MX" ? "e.g. Jalisco" : "Select a country first"
              }
              value={biz.state}
              onChange={handleStateTextChange}
              onBlur={trimTrailing("state")}
              maxLength={17}
              disabled={!biz.country}
              invalid={show("state")}
            />
            <FormatHint text="Letters, numbers, and standard punctuation · Max 17 characters" />
            <ErrorMsg msg={show("state") ? errors.state : ""} />
          </FormField>
        )}
      </div>

      <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField label="City" required>
          <TextInput
            placeholder="Joliet"
            value={biz.city}
            onChange={handleCityChange}
            onBlur={trimTrailing("city")}
            maxLength={biz.country && biz.country !== "US" ? 50 : 22}
            invalid={show("city")}
          />
          <FormatHint
            text={
              biz.country && biz.country !== "US"
                ? "Letters and spaces only · Max 50 characters"
                : FIELD_FORMATS.city
            }
          />
          <ErrorMsg msg={show("city") ? errors.city : ""} />
        </FormField>
        <FormField label="ZIP / Postal code" required>
          <TextInput
            mono={!biz.country || biz.country === "US"}
            maxLength={biz.country && biz.country !== "US" ? 16 : 13}
            placeholder={biz.country && biz.country !== "US" ? "e.g. A1B 2C3" : "60432"}
            value={biz.zip}
            onChange={handleZipChange}
            invalid={show("zip")}
          />
          <FormatHint
            text={
              biz.country && biz.country !== "US"
                ? "Letters, numbers, and standard punctuation · Max 16 characters"
                : FIELD_FORMATS.zip
            }
          />
          <ErrorMsg msg={show("zip") ? errors.zip : ""} />
        </FormField>
      </div>

      <Checkbox
        id="hasThirdPartyDesignee"
        title="Add a third-party designee"
        subtitle="Someone the IRS can contact about this return besides the business itself."
        checked={biz.hasThirdPartyDesignee}
        onChange={(e) => setBiz({ hasThirdPartyDesignee: e.target.checked })}
      />

      {biz.hasThirdPartyDesignee && (
        <>
          <FormField label="Designee name" required>
            <TextInput
              value={biz.tpdName}
              onChange={handleTpdNameChange}
              onBlur={trimTrailing("tpdName")}
              placeholder="Casey Nguyen"
              maxLength={35}
              invalid={show("tpdName")}
            />
            <FormatHint text={FIELD_FORMATS.tpdName} />
            <ErrorMsg msg={show("tpdName") ? errors.tpdName : ""} />
          </FormField>
          <div className="grid gap-3.5 sm:grid-cols-2">
            <FormField label="Designee mobile" required>
              <TextInput
                mono
                placeholder="(815) 555-0177"
                value={biz.tpdPhone}
                onChange={(e) => setBiz({ tpdPhone: formatPhone(e.target.value) })}
                invalid={show("tpdPhone")}
              />
              <FormatHint text={FIELD_FORMATS.tpdPhone} />
              <ErrorMsg msg={show("tpdPhone") ? errors.tpdPhone : ""} />
            </FormField>
            <FormField label="PIN" required>
              <TextInput
                mono
                type="password"
                maxLength={5}
                placeholder="•••••"
                value={biz.tpdPin}
                onChange={(e) => setBiz({ tpdPin: e.target.value.replace(/\D/g, "").slice(0, 5) })}
                invalid={show("tpdPin")}
              />
              <FormatHint text={FIELD_FORMATS.tpdPin} />
              <ErrorMsg msg={show("tpdPin") ? errors.tpdPin : ""} />
            </FormField>
          </div>
        </>
      )}

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          {backLabel}
        </Button>
        <Button onClick={handleContinue} disabled={saving}>
          {saving ? "Saving…" : continueLabel}
        </Button>
      </div>
    </Panel>
  );
};

export default BusinessStep;